import React, { useMemo, useState, useEffect } from 'react';
import { getApiErrorMessage } from '../../types/api-errors';
import { apiService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import type { ServiceDetailsResponse, BranchDto } from '../../types/api-responses';
import styles from './CloneServicesPanel.module.css';

/**
 * B22 — Inline "Clone services" panel.
 *
 * Replaces the previous BulkCloneServicesModal (a popup that proved
 * confusing because it auto-picked a source from the active branch tab
 * and showed services that weren't the ones the user had open).
 *
 * Now mounted inline as a sub-tab inside /my-business → Services, so
 * the operation has its own page space instead of being layered over
 * the list. Same γ-lite semantics: each cloned service becomes its
 * own independent row at the destination branch.
 *
 * Flow:
 *   1. Pick SOURCE branch — defaults to the branch with the most
 *      services (most likely starting point). All branches shown.
 *   2. Pick DESTINATION branch — defaults to empty. All branches
 *      except the source are shown (can't clone to itself).
 *   3. Services list shows the source's services grouped by category.
 *      Services already at the destination are dimmed (can't clone
 *      duplicates; BE would skip them anyway).
 *   4. Tick + click "Clone N services to {destination}".
 *
 * Hardcoded EN strings per the deferred-i18n-batch convention used
 * across /my-business. AR translations land in the i18n batch PBI.
 */

export interface CloneServicesPanelProps {
    /** Every active service across the business. */
    allServices: ServiceDetailsResponse[];
    /** Every branch in the business. */
    branches: BranchDto[];
    /** Fires after a successful clone so the parent can refresh its list. */
    onCloned: () => void;
}

const CloneServicesPanel: React.FC<CloneServicesPanelProps> = ({
    allServices,
    branches,
    onCloned,
}) => {
    const { showToast } = useToast();

    // Default source = branch with most active services. Most useful
    // starting point. User can change it via the picker.
    const defaultSourceId = useMemo(() => {
        let bestId: number | null = null;
        let bestCount = -1;
        for (const b of branches) {
            const c = allServices.filter(s => s.branchId === b.id && s.isActive).length;
            if (c > bestCount) { bestCount = c; bestId = b.id; }
        }
        return bestId;
    }, [branches, allServices]);

    const [sourceBranchId, setSourceBranchId] = useState<number | null>(defaultSourceId);
    const [targetBranchId, setTargetBranchId] = useState<number | null>(null);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [submitting, setSubmitting] = useState(false);

    // Re-seed source if the default changes (e.g., services loaded async after mount).
    useEffect(() => {
        setSourceBranchId(prev => prev ?? defaultSourceId);
    }, [defaultSourceId]);

    const sourceCandidates = useMemo(() => {
        return branches
            .map(b => ({
                ...b,
                count: allServices.filter(s => s.branchId === b.id && s.isActive).length,
            }))
            .sort((a, b) => b.count - a.count);
    }, [branches, allServices]);

    // Destination picker EXCLUDES whatever's in source (B21's "can't
    // clone to itself" symmetry).
    const targetCandidates = useMemo(() => {
        return branches.filter(b => b.id !== sourceBranchId);
    }, [branches, sourceBranchId]);

    // If source changes to whatever was destination, clear destination.
    useEffect(() => {
        if (targetBranchId != null && targetBranchId === sourceBranchId) {
            setTargetBranchId(null);
        }
    }, [sourceBranchId, targetBranchId]);

    const targetBranchName = useMemo(
        () => branches.find(b => b.id === targetBranchId)?.name ?? 'the destination',
        [branches, targetBranchId],
    );

    const sourceServices = useMemo(() => {
        if (sourceBranchId == null) return [];
        return allServices.filter(s => s.branchId === sourceBranchId && s.isActive);
    }, [allServices, sourceBranchId]);

    const targetExisting = useMemo(() => {
        const set = new Set<string>();
        for (const s of allServices) {
            if (s.branchId === targetBranchId && s.isActive) {
                set.add(`${s.categoryName ?? ''}|${(s.name ?? '').toLowerCase()}`);
            }
        }
        return set;
    }, [allServices, targetBranchId]);

    const isAlreadyAtTarget = (svc: ServiceDetailsResponse) =>
        targetExisting.has(`${svc.categoryName ?? ''}|${(svc.name ?? '').toLowerCase()}`);

    const groupedByCategory = useMemo(() => {
        const map = new Map<string, ServiceDetailsResponse[]>();
        for (const svc of sourceServices) {
            const cat = svc.categoryName || 'Uncategorized';
            if (!map.has(cat)) map.set(cat, []);
            map.get(cat)!.push(svc);
        }
        return Array.from(map.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([cat, list]) => ({
                category: cat,
                services: list.sort((a, b) => a.name.localeCompare(b.name)),
            }));
    }, [sourceServices]);

    // Reset selection when source branch changes — previous picks may
    // not map to the new source.
    useEffect(() => {
        setSelectedIds(new Set());
    }, [sourceBranchId]);

    function toggleService(id: number) {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }

    function categorySelectionState(group: { category: string; services: ServiceDetailsResponse[] }):
        'none' | 'some' | 'all' {
        const cloneable = group.services.filter(s => !isAlreadyAtTarget(s));
        if (cloneable.length === 0) return 'none';
        const picked = cloneable.filter(s => selectedIds.has(s.id)).length;
        if (picked === 0) return 'none';
        if (picked === cloneable.length) return 'all';
        return 'some';
    }

    function toggleCategory(group: { category: string; services: ServiceDetailsResponse[] }) {
        const cloneable = group.services.filter(s => !isAlreadyAtTarget(s));
        const state = categorySelectionState(group);
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (state === 'all') {
                cloneable.forEach(s => next.delete(s.id));
            } else {
                cloneable.forEach(s => next.add(s.id));
            }
            return next;
        });
    }

    async function handleClone() {
        if (sourceBranchId == null || targetBranchId == null || selectedIds.size === 0 || submitting) return;
        const ids = Array.from(selectedIds);
        if (ids.length > 50) {
            showToast('Maximum 50 services per batch. Run again for the rest.', 'error');
            return;
        }
        setSubmitting(true);
        try {
            const res = await apiService.bulkCloneServices(targetBranchId, {
                sourceBranchId,
                serviceIds: ids,
            });
            const parts: string[] = [];
            if (res.clonedCount > 0) parts.push(`Cloned ${res.clonedCount} service${res.clonedCount === 1 ? '' : 's'} to ${targetBranchName}`);
            if (res.skippedCount > 0) parts.push(`skipped ${res.skippedCount} already there`);
            showToast(parts.join(' · ') || 'Nothing to clone.', res.clonedCount > 0 ? 'success' : 'info');
            // Reset selection but keep both branches picked so the owner
            // can do another batch if they want.
            setSelectedIds(new Set());
            onCloned();
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Clone failed.'); // AB#1919 (WS-5b)
            showToast(msg, 'error');
        } finally {
            setSubmitting(false);
        }
    }

    const totalSelected = selectedIds.size;
    const destinationPicked = targetBranchId != null;
    const sourceHasServices = sourceBranchId != null && sourceServices.length > 0;

    if (branches.length < 2) {
        return (
            <div className={styles.panel}>
                <div className={styles.empty}>
                    <strong>Cloning needs at least two branches.</strong>
                    <div className={styles.emptyHint}>
                        Add another branch in <em>Branches</em>, then come back here to copy services across.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.panel}>
            <p className={styles.intro}>
                Pick services at the source branch — they'll land as independent copies at the destination. Editing one branch's copy later doesn't affect the other.
            </p>

            <div className={styles.branchPickerRow}>
                <div className={styles.branchPicker}>
                    <label htmlFor="clone-source" className={styles.branchPickerLabel}>
                        <span className={`${styles.branchPickerDot} ${styles.branchPickerDotSource}`} aria-hidden="true">●</span>
                        Copying from (source)
                    </label>
                    <select
                        id="clone-source"
                        value={sourceBranchId ?? ''}
                        onChange={e => setSourceBranchId(parseInt(e.target.value, 10) || null)}
                        disabled={submitting}
                        className={styles.branchPickerSelect}
                    >
                        <option value="">— Pick a source branch —</option>
                        {sourceCandidates.map(b => (
                            <option key={b.id} value={b.id}>
                                {b.name} — {b.count} {b.count === 1 ? 'service' : 'services'}
                            </option>
                        ))}
                    </select>
                </div>
                <div className={styles.branchPickerArrow} aria-hidden="true">→</div>
                <div className={styles.branchPicker}>
                    <label htmlFor="clone-target" className={styles.branchPickerLabel}>
                        <span className={styles.branchPickerDot} aria-hidden="true">●</span>
                        Adding to (destination)
                    </label>
                    <select
                        id="clone-target"
                        value={targetBranchId ?? ''}
                        onChange={e => setTargetBranchId(parseInt(e.target.value, 10) || null)}
                        disabled={submitting || sourceBranchId == null}
                        className={styles.branchPickerSelect}
                    >
                        <option value="">— Pick a destination branch —</option>
                        {targetCandidates.map(b => (
                            <option key={b.id} value={b.id}>
                                {b.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {sourceBranchId == null ? (
                <div className={styles.empty}>Pick a source branch above to see its services.</div>
            ) : !sourceHasServices ? (
                <div className={styles.empty}>
                    This branch has no active services to clone. Pick a different source branch.
                </div>
            ) : !destinationPicked ? (
                <div className={styles.empty}>
                    <strong>Now pick a destination branch above.</strong>
                    <div className={styles.emptyHint}>
                        Once you pick where to land, you can choose which of the {sourceServices.length} {sourceServices.length === 1 ? 'service' : 'services'} from the source to copy over.
                    </div>
                </div>
            ) : (
                <>
                    <div className={styles.dimExplainer} role="note">
                        <span aria-hidden="true">ℹ</span>
                        <span>
                            Services already at <strong>{targetBranchName}</strong> are dimmed and can't be cloned again — they'd duplicate. Tick the rest.
                        </span>
                    </div>

                    <div className={styles.tree}>
                        {groupedByCategory.map(group => {
                            const state = categorySelectionState(group);
                            const cloneableCount = group.services.filter(s => !isAlreadyAtTarget(s)).length;
                            return (
                                <div key={group.category} className={styles.group}>
                                    <button
                                        type="button"
                                        className={styles.groupHeader}
                                        onClick={() => toggleCategory(group)}
                                        disabled={cloneableCount === 0 || submitting}
                                        aria-pressed={state === 'all'}
                                    >
                                        <span
                                            className={`${styles.groupCheck} ${state === 'all' ? styles.groupCheckAll : state === 'some' ? styles.groupCheckSome : ''}`}
                                            aria-hidden="true"
                                        >
                                            {state === 'all' ? '✓' : state === 'some' ? '–' : ''}
                                        </span>
                                        <span className={styles.groupName}>{group.category}</span>
                                        <span className={styles.groupCount}>
                                            {cloneableCount > 0
                                                ? `${cloneableCount} to clone`
                                                : 'all already at destination'}
                                        </span>
                                    </button>

                                    <ul className={styles.serviceList}>
                                        {group.services.map(svc => {
                                            const alreadyThere = isAlreadyAtTarget(svc);
                                            const picked = selectedIds.has(svc.id);
                                            return (
                                                <li
                                                    key={svc.id}
                                                    className={`${styles.serviceRow} ${alreadyThere ? styles.serviceRowDimmed : ''}`}
                                                >
                                                    <label className={styles.serviceLabel}>
                                                        <input
                                                            type="checkbox"
                                                            checked={picked}
                                                            disabled={alreadyThere || submitting}
                                                            onChange={() => toggleService(svc.id)}
                                                        />
                                                        <span className={styles.serviceName}>{svc.name}</span>
                                                        <span className={styles.serviceMeta}>
                                                            {svc.minDuration}{svc.maxDuration && svc.maxDuration !== svc.minDuration ? `–${svc.maxDuration}` : ''}
                                                        </span>
                                                        {alreadyThere && (
                                                            <span className={styles.alreadyBadge}>Already at {targetBranchName}</span>
                                                        )}
                                                    </label>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            );
                        })}
                    </div>

                    <div className={styles.footer}>
                        <div className={styles.selectionCount}>
                            {totalSelected === 0
                                ? 'Nothing selected'
                                : `${totalSelected} service${totalSelected === 1 ? '' : 's'} selected`}
                            {totalSelected > 50 && (
                                <span className={styles.cap}> · cap is 50 per batch</span>
                            )}
                        </div>
                        <button
                            type="button"
                            className={styles.btnPrimary}
                            onClick={handleClone}
                            disabled={totalSelected === 0 || totalSelected > 50 || submitting || !destinationPicked}
                        >
                            {submitting
                                ? 'Cloning…'
                                : totalSelected > 0 && destinationPicked
                                    ? `Clone ${totalSelected} service${totalSelected === 1 ? '' : 's'} to ${targetBranchName}`
                                    : 'Clone'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default CloneServicesPanel;
